import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Ban,
  Check,
  Copy,
  FileCheck2,
  Loader2,
  RefreshCw,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MetricCards } from "@/components/metric-cards";
import { SearchInput } from "@/components/search-input";
import { EnumBadge } from "@/components/badges";
import {
  PatientCasesJsonSheet,
  type PatientCasesJsonSheetTarget,
} from "@/components/patient-cases-json-sheet";
import { VobBlockDialog } from "@/components/vob-block-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDebounce } from "@/hooks/use-debounce";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import config from "@/config";
import { useVobReadyGroups } from "@/hooks/use-vob-ready";
import {
  bvSuccessCategoryLabel,
  bvSuccessCategoryTone,
  shortCaseId,
} from "@/lib/intake-format";
import type { VobReadyCaseSummary, VobReadyGroup } from "@/types/vob-ready";
import { cn } from "@/utils";

function currentUtcMonth(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function vobReviewerWarningTooltip(caseSummary: VobReadyCaseSummary): string {
  const lines: string[] = ["LLM VOB reviewer"];
  const openCount = caseSummary.vob_reviewer_open_flag_count ?? 0;
  const dispositions = caseSummary.vob_reviewer_flag_dispositions ?? [];
  const openFlags = dispositions.filter(
    (item) => item.disposition?.toLowerCase() === "open",
  );

  if (openCount > 0) {
    lines.push(`${openCount} open flag${openCount === 1 ? "" : "s"}`);
  } else {
    lines.push("Warnings present");
  }

  for (const flag of openFlags) {
    const label = flag.field || flag.id || "flag";
    const note = flag.note?.trim();
    lines.push(note ? `• ${label}: ${note}` : `• ${label}`);
  }

  if (openFlags.length === 0 && openCount === 0) {
    lines.push("See reviewer diff in GCS for details.");
  }

  return lines.join("\n");
}

function vobBatchReviewerWarningTooltip(group: VobReadyGroup): string {
  const warningCases = group.cases.filter((c) => c.vob_reviewer_has_warnings);
  const lines: string[] = ["LLM VOB reviewer"];
  const totalOpen = warningCases.reduce(
    (sum, c) => sum + (c.vob_reviewer_open_flag_count ?? 0),
    0,
  );

  if (totalOpen > 0) {
    lines.push(
      `${totalOpen} open flag${totalOpen === 1 ? "" : "s"} across ${warningCases.length} case${warningCases.length === 1 ? "" : "s"}`,
    );
  } else {
    lines.push(
      `Warnings in ${warningCases.length} case${warningCases.length === 1 ? "" : "s"}`,
    );
  }

  for (const caseSummary of warningCases) {
    const openFlags = (caseSummary.vob_reviewer_flag_dispositions ?? []).filter(
      (item) => item.disposition?.toLowerCase() === "open",
    );
    if (openFlags.length === 0) {
      continue;
    }
    lines.push("");
    lines.push(`${shortCaseId(caseSummary.id)}:`);
    for (const flag of openFlags) {
      const label = flag.field || flag.id || "flag";
      const note = flag.note?.trim();
      lines.push(note ? `• ${label}: ${note}` : `• ${label}`);
    }
  }

  if (lines.length === 2 && totalOpen === 0) {
    lines.push("See reviewer diff in GCS for details.");
  }

  return lines.join("\n");
}

function VobStatusBadge({
  status,
  hasWarnings = false,
  tooltip,
}: {
  status: string;
  hasWarnings?: boolean;
  tooltip?: string;
}) {
  const showWarning = hasWarnings && status === "completed";
  const badge = (
    <EnumBadge
      value={vobStatusLabel(status)}
      semanticTone={vobStatusTone(status, hasWarnings)}
      suffixIcon={showWarning ? AlertTriangle : undefined}
    />
  );

  if (!showWarning || !tooltip) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-default">{badge}</span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-sm whitespace-pre-wrap text-xs leading-relaxed"
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function groupBatchKey(group: VobReadyGroup): string {
  return `${group.patient_id}|${group.j_code}|${group.date}`;
}

function groupVobStatus(group: VobReadyGroup): string | null {
  const statuses = group.cases
    .map((c) => c.vob_generation_status)
    .filter(Boolean) as string[];
  if (statuses.some((s) => s === "in_progress")) return "in_progress";
  if (statuses.length > 0 && statuses.every((s) => s === "completed")) return "completed";
  if (statuses.length > 0 && statuses.every((s) => s === "failed")) return "failed";
  return null;
}

function caseHasDownloadableVob(caseSummary: VobReadyCaseSummary): boolean {
  return caseSummary.vob_generation_status === "completed";
}

function groupHasGeneratedVob(group: VobReadyGroup): boolean {
  return group.cases.some(caseHasDownloadableVob);
}

function groupAnyMarkedReady(group: VobReadyGroup): boolean {
  return group.cases.some((c) => c.vob_marked_ready);
}

function groupHasVobReviewerWarnings(group: VobReadyGroup): boolean {
  return group.cases.some((c) => c.vob_reviewer_has_warnings);
}

function vobStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "in_progress":
      return "Generating";
    case "completed":
      return "VOB Generated";
    case "failed":
      return "Failed";
    default:
      return "—";
  }
}

function vobStatusTone(
  status: string | null | undefined,
  hasWarnings = false,
): "success" | "failure" | "warning" | "info" | "purple" | "neutral" | "muted" {
  switch (status) {
    case "in_progress":
      return "info";
    case "completed":
      return hasWarnings ? "warning" : "purple";
    case "failed":
      return "failure";
    default:
      return "muted";
  }
}

function CopyCaseIdButton({ caseId }: { caseId: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
      aria-label={`Copy case ID ${caseId}`}
      title={copied ? "Copied" : "Copy case ID"}
      onClick={() => {
        void navigator.clipboard.writeText(caseId).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-600" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </Button>
  );
}

export default function VobReadyPage() {
  const [monthFilter, setMonthFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMarkedReady, setShowMarkedReady] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [casesPanelTarget, setCasesPanelTarget] =
    useState<PatientCasesJsonSheetTarget | null>(null);
  const [blockDialogGroup, setBlockDialogGroup] = useState<VobReadyGroup | null>(
    null,
  );
  const debouncedSearch = useDebounce(searchTerm);

  const scrollSentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    groups,
    loading,
    loadingMore,
    error,
    hasMore,
    caseCount,
    filteredGroupCount,
    markedReadyGroupCount,
    notMarkedReadyGroupCount,
    loadMore,
    refresh,
    generateVob,
    markVobReady,
    unmarkVobReady,
    blockVob,
    generatingKeys,
    markingReadyKeys,
    unmarkingReadyKeys,
    blockingKeys,
    generateError,
  } = useVobReadyGroups({
    month: monthFilter || undefined,
    search: debouncedSearch,
    showMarkedReady,
    showBlocked,
  });

  useInfiniteScroll(scrollSentinelRef, loadMore, {
    enabled: hasMore && !loading && !loadingMore,
    rootKey: groups.length,
  });

  const metrics = useMemo(
    () => [
      {
        label: "VOB-ready cases",
        value: caseCount,
        icon: Users,
      },
      {
        label: showBlocked
          ? "Blocked batches"
          : showMarkedReady
            ? "Marked-ready batches"
            : "Total batches",
        value: filteredGroupCount,
        icon: FileCheck2,
      },
    ],
    [caseCount, filteredGroupCount, showMarkedReady, showBlocked],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">VOB Ready</h1>
          <p className="text-sm text-muted-foreground">
            Terminal BV cases grouped by patient, J code, and creation month
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <MetricCards metrics={metrics} loading={loading} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search patient, J code, case id…"
          className="sm:max-w-sm"
        />
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="vob-month" className="text-sm text-muted-foreground whitespace-nowrap">
            Month (UTC)
          </label>
          <Input
            id="vob-month"
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="w-[180px]"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "text-xs font-medium",
              showMarkedReady &&
                "border-emerald-500/50 bg-emerald-500/[0.12] text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100",
            )}
            onClick={() => {
              setShowBlocked(false);
              setShowMarkedReady((v) => !v);
            }}
            disabled={loading}
            aria-pressed={showMarkedReady && !showBlocked}
            title={
              showMarkedReady
                ? "Show VOB packages not yet marked ready"
                : "Show VOB packages marked ready for customer delivery"
            }
          >
            {showMarkedReady && !showBlocked ? (
              <>
                Show not marked ready
                {notMarkedReadyGroupCount > 0
                  ? ` (${notMarkedReadyGroupCount})`
                  : ""}
              </>
            ) : (
              <>
                Show marked ready
                {markedReadyGroupCount > 0 ? ` (${markedReadyGroupCount})` : ""}
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "text-xs font-medium",
              showBlocked &&
                "border-rose-500/50 bg-rose-500/[0.12] text-rose-900 dark:border-rose-500/40 dark:bg-rose-950/40 dark:text-rose-100",
            )}
            onClick={() => {
              setShowMarkedReady(false);
              setShowBlocked((v) => !v);
            }}
            disabled={loading}
            aria-pressed={showBlocked}
            title={
              showBlocked
                ? "Back to VOB-ready batches"
                : "Show blocked VOB batches"
            }
          >
            <Ban className="mr-2 h-4 w-4" />
            {showBlocked ? "Show VOB ready" : "Show blocked"}
          </Button>
        </div>
      </div>

      {(error || generateError) && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error || generateError}
        </div>
      )}

      {loading && groups.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : filteredGroupCount === 0 ? (
        <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
          {debouncedSearch.trim()
            ? `No ${showBlocked ? "blocked" : "VOB-ready"} batches match your search`
            : `No ${showBlocked ? "blocked" : "VOB-ready"} batches found${monthFilter ? ` for ${monthFilter}` : ""}.`}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
          {showMarkedReady
            ? "No matching batches are marked ready."
            : "No matching batches awaiting mark-as-ready."}
          {showMarkedReady && notMarkedReadyGroupCount > 0
            ? ` Use “Show not marked ready” to view ${notMarkedReadyGroupCount} other batch${notMarkedReadyGroupCount === 1 ? "" : "es"}.`
            : !showMarkedReady && markedReadyGroupCount > 0
              ? ` Use “Show marked ready” to view ${markedReadyGroupCount} marked batch${markedReadyGroupCount === 1 ? "" : "es"}.`
              : ""}
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const batchKey = groupBatchKey(group);
            const batchStatus = groupVobStatus(group);
            const batchHasWarnings = groupHasVobReviewerWarnings(group);
            const isGenerating = generatingKeys.has(batchKey);
            const hasGeneratedVob = groupHasGeneratedVob(group);
            const allMarkedReady = groupAnyMarkedReady(group);
            const isMarkingReady = markingReadyKeys.has(batchKey);
            const isUnmarkingReady = unmarkingReadyKeys.has(batchKey);
            const isBlocking = blockingKeys.has(batchKey);
            const downloadCaseId = group.cases.find(caseHasDownloadableVob)?.id;

            return (
            <article
              key={`${group.patient_id}-${group.j_code}-${group.date}`}
              className="rounded-lg border bg-card p-4 shadow-sm"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold">
                      {group.patient_name || "Unknown patient"}
                    </h2>
                    <EnumBadge value={group.j_code} semanticTone="info" />
                    <EnumBadge value={group.date} semanticTone="neutral" />
                    <EnumBadge
                      value={`${group.case_count} case${group.case_count === 1 ? "" : "s"}`}
                      semanticTone="success"
                    />
                    {batchStatus ? (
                      <VobStatusBadge
                        status={batchStatus}
                        hasWarnings={batchHasWarnings}
                        tooltip={
                          batchHasWarnings
                            ? vobBatchReviewerWarningTooltip(group)
                            : undefined
                        }
                      />
                    ) : null}
                    {allMarkedReady ? (
                      <EnumBadge value="Marked ready" semanticTone="success" />
                    ) : null}
                    {showBlocked ? (
                      <EnumBadge value="Blocked" semanticTone="failure" />
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    patient_id: {group.patient_id}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => void generateVob(group)}
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating…
                      </>
                    ) : hasGeneratedVob ? (
                      "Re-generate VOB"
                    ) : (
                      "Generate VOB"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setCasesPanelTarget({
                        patient_id: group.patient_id,
                        patient_name: group.patient_name,
                        j_code: group.j_code,
                        date: group.date,
                      })
                    }
                  >
                    View cases
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-200 text-rose-900 hover:bg-rose-50 dark:border-rose-900/50 dark:text-rose-100 dark:hover:bg-rose-950/40"
                    disabled={isBlocking}
                    onClick={() => setBlockDialogGroup(group)}
                  >
                    {isBlocking ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Blocking…
                      </>
                    ) : (
                      <>
                        <Ban className="mr-2 h-4 w-4" />
                        Block
                      </>
                    )}
                  </Button>
                  {hasGeneratedVob && allMarkedReady ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void unmarkVobReady(group)}
                      disabled={isUnmarkingReady}
                    >
                      {isUnmarkingReady ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Unmarking…
                        </>
                      ) : (
                        "Unmark ready"
                      )}
                    </Button>
                  ) : hasGeneratedVob ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void markVobReady(group)}
                      disabled={isMarkingReady}
                    >
                      {isMarkingReady ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Marking…
                        </>
                      ) : (
                        "Mark as ready"
                      )}
                    </Button>
                  ) : null}
                  {downloadCaseId ? (
                    <Button size="sm" variant="outline" asChild>
                      <a
                        href={`${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(downloadCaseId)}/vob-download`}
                        download
                      >
                        Download VOB files
                      </a>
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-2 py-2">Case</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Success category</th>
                      <th className="px-2 py-2">Payer</th>
                      <th className="px-2 py-2">Created</th>
                      <th className="px-2 py-2">Stedi</th>
                      <th className="px-2 py-2">VOB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.cases.map((c: VobReadyCaseSummary) => (
                      <tr key={c.id} className="border-b last:border-0">
                        <td className="px-2 py-2 font-mono text-xs">
                          <div className="flex items-center gap-0.5">
                            <Link
                              to={`/cases?case_id=${encodeURIComponent(c.id)}`}
                              className="text-primary hover:underline"
                              title={c.id}
                            >
                              {shortCaseId(c.id)}
                            </Link>
                            <CopyCaseIdButton caseId={c.id} />
                          </div>
                        </td>
                        <td className="px-2 py-2">{c.call_status}</td>
                        <td className="px-2 py-2">
                          {c.success_category && c.success_category !== "none" ? (
                            <EnumBadge
                              value={bvSuccessCategoryLabel(c.success_category)}
                              semanticTone={bvSuccessCategoryTone(c.success_category)}
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-2 py-2">{c.payer_name || "—"}</td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-2 py-2">
                          {c.has_stedi_eligibility_response ? "Yes" : "No"}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-col gap-0.5">
                            {c.vob_generation_status ? (
                              <VobStatusBadge
                                status={c.vob_generation_status}
                                hasWarnings={c.vob_reviewer_has_warnings}
                                tooltip={
                                  c.vob_reviewer_has_warnings
                                    ? vobReviewerWarningTooltip(c)
                                    : undefined
                                }
                              />
                            ) : (
                              <span>—</span>
                            )}
                            {c.vob_generation_status === "failed" &&
                            c.vob_generation_error ? (
                              <span
                                className="text-xs text-destructive max-w-[240px] truncate"
                                title={c.vob_generation_error}
                              >
                                {c.vob_generation_error}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
            );
          })}
          <div ref={scrollSentinelRef} className="h-px" aria-hidden />
          {loadingMore ? (
            <div className="flex justify-center py-4 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading more batches…
            </div>
          ) : hasMore ? (
            <p className="py-2 text-center text-xs text-muted-foreground">
              Showing {groups.length} of {filteredGroupCount} matching batches
            </p>
          ) : groups.length > 0 ? (
            <p className="py-2 text-center text-xs text-muted-foreground">
              All {filteredGroupCount} matching batches loaded
            </p>
          ) : null}
        </div>
      )}

      <PatientCasesJsonSheet
        target={casesPanelTarget}
        onClose={() => setCasesPanelTarget(null)}
      />

      <VobBlockDialog
        group={blockDialogGroup}
        open={blockDialogGroup != null}
        running={
          blockDialogGroup != null &&
          blockingKeys.has(groupBatchKey(blockDialogGroup))
        }
        onOpenChange={(open) => {
          if (!open) setBlockDialogGroup(null);
        }}
        onConfirm={async (blockDetails) => {
          if (!blockDialogGroup) return;
          await blockVob(blockDialogGroup, blockDetails);
          setBlockDialogGroup(null);
        }}
      />
    </div>
    </TooltipProvider>
  );
}
